import React, { useContext, useEffect, useState } from "react";
import { Title } from "../../../../../stories/Title/Title";
import Close from "../../../../../images/Close.svg";
import { Label } from "../../../../../stories/Label/Label";
import TiptapEditor from "../../../../../styles/TipTapEditor";
import { useEditor } from "@tiptap/react";
import TextAlign from "@tiptap/extension-text-align";
import Color from "@tiptap/extension-color";
import TextStyle from "@tiptap/extension-text-style";
import BulletList from "@tiptap/extension-bullet-list";
import Document from "@tiptap/extension-document";
import ListItem from "@tiptap/extension-list-item";
import OrderedList from "@tiptap/extension-ordered-list";
import Paragraph from "@tiptap/extension-paragraph";
import Text from "@tiptap/extension-text";
import StarterKit from "@tiptap/starter-kit";
import EditTiptapEditor from "../../../../../styles/EditTipTapEditor";
import { Button } from "../../../../../stories/Button/Button";
import { empType, Operation } from "../../../../../utils/Enum.types";
import { idAndFlagType } from "../../../../../utils/formTypes";
import {
  allowedFormatsForMom,
  emptyIdAndModal,
  emptyMessage
} from "../../../../../utils/constants";
import { useLazyQuery, useMutation } from "@apollo/client";
import {
  GetMomDetails,
  getMomDetailsByNodeId,
  GetVendorNotes
} from "../queries/query";
import {
  AddVendorNotesResult,
  AddVendorNotesVars,
  AtsMomDetails,
  UpdateVendorNotesResult,
  UpdateVendorNotesVars
} from "../queries/types";
import { nodeVar } from "../../../../../utils/commonQueryTypes";
import { AddVendorNotes, UpdateVendorNotes } from "../queries/mutations";
import useUrlParams from "../../../../../customHooks/useUrlParams";
import useLoggedInUserDetails from "../../../../../customHooks/useLoggedInUserDetails";
import { handleUploadAndDownloadFile } from "../../../../../utils/Upload";
import { msgType } from "../../../../../utils/types";
import MessageModal from "../../../../../customComponents/MessageModal";
import { getDownloadUrl } from "../../../../../utils/DownloadFile";
import { AppContext } from "../../../../../context/context";
import axios from "axios";
interface Props {
  setModal: React.Dispatch<React.SetStateAction<idAndFlagType>>;
  idModalFlag: idAndFlagType;
  operation: Operation;
}
const AddMeetingNote = ({ setModal, operation, idModalFlag }: Props) => {
  const [comment, setComment] = useState("");

  const [GetMom, { data }] = useLazyQuery<{ node: AtsMomDetails }, nodeVar>(
    getMomDetailsByNodeId
  );
  const [message, setMessage] = useState(emptyMessage);
  const { customer_id, momID, projectID } = useUrlParams();
  const { user_details } = useLoggedInUserDetails();

  const [progress, setProgress] = useState(0);

  const { state } = useContext(AppContext);

  const [AddNotes, { loading }] = useMutation<
    AddVendorNotesResult,
    AddVendorNotesVars
  >(AddVendorNotes, {
    onError: (e) => {
      setMessage({
        flag: true,
        message: e.message,
        operation: Operation.NONE
      });
    }
  });

  const [updateVendor, { loading: updateLoading }] = useMutation<
    UpdateVendorNotesResult,
    UpdateVendorNotesVars
  >(UpdateVendorNotes, {
    onError: (e) => {
      setMessage({
        flag: true,
        message: e.message,
        operation: Operation.NONE
      });
    }
  });

  const editor = useEditor({
    extensions: [
      StarterKit,
      Document,
      Paragraph,
      Text,
      BulletList,
      OrderedList,
      ListItem,
      TextAlign.configure({
        types: ["heading", "paragraph"]
      }),
      TextStyle,
      Color
    ],
    onUpdate: ({ editor }) => {
      // Get HTML content and store in state
      setComment(editor.getHTML());
    },
    content: comment
  });
  const [editSelectedFiles, setEditSelectedFiles] = useState<File[]>([]);
  useEffect(() => {
    if (idModalFlag.id && operation === Operation.UPDATE) {
      GetMom({
        variables: {
          id: idModalFlag.id
        }
      }).then(({ data }) => {
        if (data && data.node) {
          setComment(data.node.vendorNotes);
          editor?.commands.setContent(data.node.vendorNotes);
        }
      });
    }
  }, [idModalFlag.id, GetMom, data, operation, editor]);

  const handleClear = () => {
    setComment("");
    editor?.commands.clearContent();
    setEditSelectedFiles([]);
  };

  const handleSave = () => {
    if (momID && projectID) {
      let filename = "";
      if (editSelectedFiles) {
        filename = editSelectedFiles
          .map((file, index) => {
            return `${customer_id}/momDetails/${momID}/${projectID}/${file.name}`;
          })
          .join(";");
      }

      if (operation === Operation.CREATE) {
        AddNotes({
          variables: {
            customerID: customer_id!,
            user_details,
            momID,
            projectID,
            input: [
              {
                vendorNotes: comment,
                vendorNotesDocuments: filename
              }
            ]
          },
          refetchQueries: [
            {
              query: getMomDetailsByNodeId,
              variables: {
                id: idModalFlag.id
              }
            },
            {
              query: GetMomDetails,
              variables: {
                customerID: customer_id,
                momID,
                projectID,
                latest: false
              }
            },
            {
              query: GetVendorNotes,
              variables: {
                customerID: customer_id,
                momID,
                projectID,
                latest: true
              }
            }
          ]
        }).then(({ data }) => {
          if (data && data.AddVendorNotes && data.AddVendorNotes.length) {
            if (editSelectedFiles) {
              const uploadFilePromise = Promise.all(
                editSelectedFiles.map((file, index) => {
                  const fileExt = file.name.split(".").pop();
                  handleUploadAndDownloadFile(
                    file,
                    `${customer_id}/momDetails/${momID}/${projectID}/vendorNotes/${data.AddVendorNotes[0].id}_${index}.${fileExt}`,
                    setProgress,
                    false
                  );
                })
              );

              Promise.all([uploadFilePromise]).then(([sendMsgData]) => {
                if (sendMsgData) {
                  setMessage({
                    flag: true,
                    message: "Comment added successfully",
                    operation: Operation.CREATE
                  });
                }
              });
            }
          }
        });
      } else {
        updateVendor({
          variables: {
            customerID: customer_id!,
            user_details,
            momDetailsID: idModalFlag.id,
            input: {
              vendorNotes: comment,
              vendorNotesDocuments: filename
            }
          },
          refetchQueries: [
            {
              query: getMomDetailsByNodeId,
              variables: {
                id: idModalFlag.id
              }
            },
            {
              query: GetVendorNotes,
              variables: {
                customerID: customer_id,
                momID,
                projectID,
                latest: true
              }
            }
          ]
        }).then(({ data }) => {
          if (data && data.UpdateVendorNotes) {
            if (editSelectedFiles) {
              const uploadFilePromise = Promise.all(
                editSelectedFiles.map((file, index) => {
                  const fileExt = file.name.split(".").pop();
                  handleUploadAndDownloadFile(
                    file,
                    `${customer_id}/momDetails/${momID}/${projectID}/vendorNotes/${idModalFlag.id}_${index}.${fileExt}`,
                    setProgress,
                    false
                  );
                })
              );

              Promise.all([uploadFilePromise]).then(([sendMsgData]) => {
                if (sendMsgData) {
                  setMessage({
                    flag: true,
                    message: "Comment updated successfully",
                    operation: Operation.UPDATE
                  });
                }
              });
            }
          }
        });
      }
    }
  };

  const handleClose = () => {
    if (message.operation !== Operation.NONE) {
      handleClear();
      setModal(idModalFlag);
    }
    setMessage(emptyMessage);
  };

  useEffect(() => {
    const fetchData = async () => {
      if (
        data &&
        data.node.vendorNotesDocuments &&
        state.empSubType === empType.VENDOR_MANAGER
      ) {
        const files = data.node.vendorNotesDocuments.split(";");

        let index = 0;
        for (const f of files) {
          const ext = f.split(".").pop();

          const filename = `${customer_id}/momDetails/${momID}/${projectID}/vendorNotes/${data.node.id}_${index}.${ext}`;
          const ers = await getDownloadUrl(filename, false);

          const blob = await axios
            .get(ers, { responseType: "blob" })
            .then((response) => {
              return new File([response.data], `attachement_${index}.${ext}`, {
                type: response.data.type
              });
            });

          setEditSelectedFiles((prev) => [...prev, blob]);
          index++;
        }
      }
    };
    fetchData();
  }, [data, state.empSubType]);

  return (
    <div className="edit-project-meeting">
      <div className="edit-project-meeting__title">
        <Title>{`${
          operation === Operation.UPDATE ? "Update" : "Add"
        } new Minutes of Meeting Points`}</Title>
        <img src={Close} alt="" onClick={() => setModal(emptyIdAndModal)} />
      </div>
      <div className="edit-project-meeting__meeting-note">
        {/* <Label>Description</Label> */}
        <div className="edit-project-meeting__meeting-note--text-area">
          <EditTiptapEditor
            commentState={{ comment, setComment }}
            editor={editor}
            filesState={{ editSelectedFiles, setEditSelectedFiles }}
          />
        </div>
      </div>
      <Button
        mode="save"
        onClick={handleSave}
        loading={loading || updateLoading}
      />
      <Button mode="clear" loading={loading || updateLoading} />
      <Button
        mode="cancel"
        onClick={() => setModal(emptyIdAndModal)}
        loading={loading || updateLoading}
      />

      <MessageModal
        handleClose={handleClose}
        modalFlag={message.flag}
        operation={message.operation}
        value={message.message}
      />
    </div>
  );
};

export default AddMeetingNote;
